import React, { useState } from 'react';
import {
  Alert,
  IconButton,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography as MuiTypography,
  CircularProgress
} from '@mui/material';
import {
  AutoAwesome as AIIcon,
  Close as CloseIcon,
  Key as KeyIcon
} from '@mui/icons-material';
import { useMIA } from '../../../../context/MIAContext';
import { useTheme } from '../../../../design-system/providers/ThemeProvider';
import PlatformDialog from '../../../molecules/PlatformDialog/PlatformDialog';
import PlatformDialogTitle from '../../../molecules/PlatformDialog/PlatformDialogTitle';

/**
 * MIAActivationDialog - Dialog para ativar o MIA com token
 *
 * Solicita o token fornecido pelo time Mensure para ativar
 * as funcionalidades de IA.
 */
const MIAActivationDialog = ({ open, onClose }) => {
  const { activate } = useMIA();
  const { gradients, miaColors } = useTheme();
  const [token, setToken] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleActivate = async () => {
    if (!token.trim()) {
      setError('Digite o token de ativação');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const result = await activate(token.trim());
      if (result.success) {
        setToken('');
        onClose();
      } else {
        setError(result.error || 'Token inválido ou expirado');
      }
    } catch (err) {
      setError('Erro ao validar token. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setToken('');
    setError('');
    onClose();
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !loading) {
      handleActivate();
    }
  };

  return (
    <PlatformDialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
    >
      <PlatformDialogTitle
         title="Ativar MIA"
         subtitle="Mensure Inteligência Artificial"
         icon={<AIIcon />}
         onClose={handleClose}
         sx={{
           background: gradients.mia,
           color: 'white',
           '& .MuiTypography-root': { color: 'white' },
           '& .MuiIconButton-root': { color: 'white' }
         }}
      />

      <DialogContent sx={{ pt: 3 }}>
        <MuiTypography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
          Para ativar as funcionalidades de IA, insira o token fornecido pelo time Mensure.
        </MuiTypography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <TextField
          autoFocus
          fullWidth
          label="Token de Ativação"
          placeholder="XXXX-XXXX-XXXX-XXXX"
          value={token}
          onChange={(e) => setToken(e.target.value.toUpperCase())}
          onKeyPress={handleKeyPress}
          disabled={loading}
          InputProps={{
            startAdornment: (
              <KeyIcon sx={{ mr: 1, color: 'text.disabled' }} />
            )
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              fontFamily: 'monospace',
              letterSpacing: '0.1em'
            }
          }}
        />

        <Box sx={{ mt: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
          <MuiTypography variant="caption" sx={{ color: 'text.secondary' }}>
            <strong>Funcionalidades incluídas:</strong>
          </MuiTypography>
          <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2, '& li': { fontSize: '0.75rem' } }}>
            <li>Análise de Sentimento com IA</li>
            <li>Propagação de Sentimento</li>
            <li>Tags Automáticas</li>
            <li>Detecção de Similaridade</li>
            <li>Scraping Inteligente</li>
            <li>Resumo Automático</li>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2, pt: 0 }}>
        <Button onClick={handleClose} disabled={loading}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={handleActivate}
          disabled={loading || !token.trim()}
          sx={{
            bgcolor: miaColors.primary,
            '&:hover': { bgcolor: miaColors.secondary }
          }}
          startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <AIIcon />}
        >
          {loading ? 'Validando...' : 'Ativar MIA'}
        </Button>
      </DialogActions>
    </PlatformDialog>
  );
};

export default MIAActivationDialog;

